import * as tree_sitter from 'web-tree-sitter'
import assert from 'assert';
import { Representation } from '../representation';
import { NarrowedInterpretable } from '../interpretable';
import { TextRange } from '../text_range';

/** Start to initialize tree_sitter on load, it will be awaited on in `initialize()` */
// @ts-ignore getting an error here because of misconfiguration of the linter, but parcel can handle it
const treeSitter = tree_sitter.init({
    locateFile(scriptName: string, scriptDirectory: string) {
        return "public/tree-sitter.wasm"
    }
})

/**
 * Represents a converter back and forth between program language text and an Interpretable AST.
 * Meant for languages like Python and Java.
 */
export abstract class TreeSitterLanguage extends Representation<string> {
    /**
     * Name of this language. Should be lowercase.
     */
    abstract readonly name: string;
    /**
     * Internal tree-sitter parser this language uses
     */
    protected treeSitterParser: tree_sitter;

    /**
     * Used as a keep for all interpretables. Cleared on every interpretation request.
     */
    interpretables: Map<string, NarrowedInterpretable>

    /**
     * Maps each interpretable to their own text range.
     */
    textRange: Map<string, TextRange>

    /**
     * A set of all errors the program encountered.
     */
    errors: {range: TextRange, message: string, severity: "error"|"warn"|"tip"}[]

    

    /**
     * Create a new instance of this Language. You must call `initialize()` right after and await
     * that Promise before calling any methods.
     */
    public constructor() {        
        super();
        this.interpretables = new Map();
    }

    /**
     * Initialize this Language so it's ready to translate back and forth.
     * May fail with an exception.
     */
    public abstract initialize(): Promise<void> // Superclasses implementing this: see `initializeParserForLanguage`

    /**
     * Helper method to initialize the treeSitterParser field.
     * Superclasses implementing `initialize()` should call this.
     * @param language used to request the wasm file from api/tree-sitter-LANGUAGE.wasm
     */
    protected async initializeParserForLanguage(language: string) {
        // neccesary! removing will cause a race condition. not sure why we need two awaits, but
        // I do know it'll break without it.
        await treeSitter; 
        const ts_python_lang = await tree_sitter.Language.load(`public/tree-sitter-${language}.wasm`)
        // @ts-ignore getting an error here because of misconfiguration of the linter, but parcel can handle it
        const parser = new tree_sitter();
        parser.setLanguage(ts_python_lang);
        this.treeSitterParser = parser;
    }

    /**
     * Converts from a tree_sitter parse tree to an Interpretable AST.
     * Does this recursively for all children. Is allowed to mutate `this.interpretables`.
     * @param node any tree_sitter SyntaxNode
     * @returns UUID of an Interpretable representing the root node
     */
    protected abstract fromTreeSitter(node: tree_sitter.SyntaxNode | null, parentUUID: null|string): string | undefined
    
    /**
     * Converts a text representing either all or part of a program into an Interpretable
     * @param programText text in this language
     * @returns a map of all new interpretables.
     */
    fromRepresentation(programText: string): Map<string, NarrowedInterpretable> {
        const tree_sitter_parsed = this.treeSitterParser.parse(programText);
        // reset without clear() - that way if the caller has a reference to it we don't reset it
        // and mutate their data.
        this.interpretables = new Map();
        this.textRange = new Map();
        this.fromTreeSitter(tree_sitter_parsed.rootNode, null);
        return this.interpretables;
    }
    
    /**
     * Converts an Interpretable to text in this language.
     * @param interpretable part or all of an AST.
     * @returns a string representing the program node
     */
    abstract toRepresentation(interpretable: NarrowedInterpretable): string

    /**
     * Helper method to convert a tree-sitter node's child to an Interpretable.
     * For example, if your `node` is an if statement, you can use this method to get its condition.
     * Will throw an error if it's wrong
     * @param node the parent node
     * @param fieldName the child's name
     * @returns UUID of an Interpretable
     */
    protected convertChild(node: tree_sitter.SyntaxNode, fieldName: string, parentUUID: string): string {
        const result = this.fromTreeSitter(node.childForFieldName(fieldName), parentUUID);
        assert(result, `non-optional ${fieldName} was not found in node of type ${node.type}.`)
        return result;
    }

    /**
     * Same as convertChild, however it may return undefined if not found / child has an error.
     * @param node the parent node
     * @param fieldName the child's name
     * @returns UUID of an Interpretable or undefined.
     */
    protected convertOptionalChild(node: tree_sitter.SyntaxNode, fieldName: string, parentUUID: string): string | undefined {
        return this.fromTreeSitter(node.childForFieldName(fieldName), parentUUID);
    }

    protected addInterpretable(uuid: string, interpretable: NarrowedInterpretable, textRange: TextRange) {
        this.interpretables.set(uuid, interpretable);
        this.textRange.set(uuid, textRange);
    }

}